"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import {
  StatsMetaline,
  type StatsMetalineItem,
} from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"
import { AgentWorkQueuePanel } from "@/components/dashboard/agent-work-queue"
import { canUseAgentWorkQueue } from "@/lib/agent-work-queue-api"
import { MyTasksWidget } from "@/components/dashboard/my-tasks-widget"
import { TargetProgressWidget } from "@/components/dashboard/target-progress-widget"
import { LeaderboardWidget } from "@/components/dashboard/leaderboard-widget"
import { LeadsStatusChart } from "@/components/dashboard/leads-status-chart"
import { TodayScheduleTimeline } from "@/components/dashboard/today-schedule-timeline"
import { FollowUpsWidget } from "@/components/dashboard/variants/agent/follow-ups-widget"
import { useDashboardWidgetFlags } from "@/components/dashboard/use-dashboard-widget-flags"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { cn } from "@/lib/utils"

import { getLeadStats } from "@/lib/leads-api"
import { getAppointmentStats } from "@/lib/appointments-api"
import { getQuoteStats } from "@/lib/quotes-api"
import { getMyProgress, refreshMyProgress, getLeaderboard } from "@/lib/targets-api"
import { getDueReminders } from "@/lib/reminders-api"
import { getMyCommission } from "@/lib/hr-payroll-api"
import { formatCurrencyStatic } from "@/hooks/use-currency"
import type {
  LeadStats,
  AppointmentStats,
  QuoteStats,
  Reminder,
} from "@/types"
import type { MyProgressResponse, LeaderboardResponse } from "@/lib/targets-api"
import type { MyCommission } from "@/lib/hr-types"

const LEAD_VIEW_PERMISSIONS = ["leads.view.assigned", "leads.view.all"]

/**
 * Page size of the follow-ups rail. Doubles as the ceiling of the "follow-ups
 * due" KPI tile — the rail and the tile share ONE request, so the tile reads
 * "what is waiting for me right now", capped at the rail's depth.
 * /notifications remains the exhaustive list.
 */
const DUE_REMINDERS_LIMIT = 5

interface AgentDashboardData {
  leadStats: LeadStats | null
  appointmentStats: AppointmentStats | null
  quoteStats: QuoteStats | null
  myProgress: MyProgressResponse | null
  leaderboard: LeaderboardResponse | null
  reminders: Reminder[] | null
  myCommission: MyCommission | null
}

const EMPTY_DATA: AgentDashboardData = {
  leadStats: null,
  appointmentStats: null,
  quoteStats: null,
  myProgress: null,
  leaderboard: null,
  reminders: null,
  myCommission: null,
}

/**
 * Agent ("My day") dashboard — the bespoke variant for the sales role.
 *
 * Built around the three things an agent actually works off: the target they
 * are chasing, the follow-ups they owe, and today's bookings. Everything is
 * first-person: agents hold `leads.view.assigned`, so the server-side global
 * scopes already return THEIR slice of leads / quotes / appointments and the
 * figures can be labelled "my …" without any client-side filtering.
 *
 * Deviations from the generic variant:
 *   • the shared `<DashboardStats />` band is replaced by a personal KPI strip
 *     (my leads / follow-ups due / today's appointments / quotes awaiting a
 *     reply) whose follow-ups tile turns red the moment anything is due;
 *   • the target widget is the hero (2 of 3 columns) rather than a side card;
 *   • charts collapse to the single pipeline donut — the agent's own funnel.
 *
 * Gating follows the house contract: nothing is requested until
 * `usePermissions().isLoaded`, each call is skipped (left `null`) without its
 * grant, and a widget whose data was never fetched is OMITTED rather than
 * rendered empty.
 */
export function AgentDashboard() {
  const userId = useAuthStore((state) => state.user?.id)
  return <AgentDashboardContent key={userId ?? 'loading'} />
}

function AgentDashboardContent() {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { isLoaded, can, canAny } = usePermissions()
  const { showLeadsStatusChart } = useDashboardWidgetFlags()
  const myUserId = useAuthStore((state) => state.user?.id)
  const queueEnabled = useAuthStore((state) => canUseAgentWorkQueue(state.user))
  const commissionsEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.agent_commissions_enabled ?? false,
  )

  const [data, setData] = useState<AgentDashboardData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(true)

  const canViewLeads = canAny(LEAD_VIEW_PERMISSIONS)
  const canViewAppointments = can("appointments.view")
  const canViewQuotes = can("quotes.view")
  const canViewTargets = can("targets.view")
  const canViewReminders = can("reminders.view")
  // Own running commission — permission AND the per-clinic flag (the endpoint
  // 404s where the flag is off).
  const canViewCommission = commissionsEnabled && can("commissions.view.own")

  useEffect(() => {
    // Hold every request until the permission set has resolved — firing first
    // and discarding would 403 for users who legitimately lack the grant.
    if (!isLoaded) return

    let cancelled = false
    const loadDashboard = async () => {
      setIsLoading(true)
      try {
        const [
          leadStats,
          appointmentStats,
          quoteStats,
          myProgress,
          leaderboard,
          reminders,
          myCommission,
        ] = await Promise.all([
          canViewLeads ? getLeadStats().catch(() => null) : Promise.resolve(null),
          canViewAppointments
            ? getAppointmentStats().catch(() => null)
            : Promise.resolve(null),
          canViewQuotes ? getQuoteStats().catch(() => null) : Promise.resolve(null),
          canViewTargets ? getMyProgress().catch(() => null) : Promise.resolve(null),
          canViewTargets ? getLeaderboard().catch(() => null) : Promise.resolve(null),
          canViewReminders
            ? getDueReminders(DUE_REMINDERS_LIMIT).catch(() => null)
            : Promise.resolve(null),
          canViewCommission
            ? getMyCommission().catch(() => null)
            : Promise.resolve(null),
        ])
        if (!cancelled) {
          setData({
            leadStats,
            appointmentStats,
            quoteStats,
            myProgress,
            leaderboard,
            reminders,
            myCommission,
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    loadDashboard()

    return () => {
      cancelled = true
    }
  }, [
    isLoaded,
    canViewLeads,
    canViewAppointments,
    canViewQuotes,
    canViewTargets,
    canViewReminders,
    canViewCommission,
  ])

  // Same refresh wiring as the generic variant: recalculate server-side, then
  // re-read so the widget shows the freshly computed figures.
  const handleRefreshProgress = useCallback(async () => {
    await refreshMyProgress()
    const newProgress = await getMyProgress()
    setData((prev) => ({ ...prev, myProgress: newProgress }))
  }, [])

  // Localized "today" for the header eyebrow — this dashboard is a day view.
  const today = formatter.dateTime(new Date(), {
    weekday: "long",
    day: "numeric",
    month: "long",
  })

  const dueCount = data.reminders?.length ?? 0

  // Personal KPI band — one tile per granted source, in the agent's own order
  // of attention. Ungranted tiles are omitted (never zero-filled).
  const statItems = useMemo<StatsMetalineItem[]>(() => {
    const skeletonValue = <Skeleton className="h-[13px] w-12" />
    const items: StatsMetalineItem[] = []

    if (canViewLeads) {
      items.push({
        key: "myLeads",
        label: t("agentKpi.myLeads"),
        value: isLoading ? skeletonValue : (data.leadStats?.total ?? 0),
        sub: isLoading
          ? undefined
          : t("agentKpi.myLeadsHint", { count: data.leadStats?.this_week ?? 0 }),
      })
    }

    if (canViewReminders) {
      items.push({
        key: "followUps",
        label: t(queueEnabled ? "followUps.legacyTitle" : "agentKpi.followUps"),
        value: isLoading ? skeletonValue : dueCount,
        accent: !isLoading && dueCount > 0 ? "danger" : "default",
        sub: isLoading
          ? undefined
          : dueCount > 0
            ? t("agentKpi.followUpsHint")
            : t("agentKpi.followUpsClear"),
      })
    }

    if (canViewAppointments) {
      items.push({
        key: "todayAppointments",
        label: t("agentKpi.todayAppointments"),
        value: isLoading ? skeletonValue : (data.appointmentStats?.today ?? 0),
        sub: isLoading
          ? undefined
          : t("agentKpi.todayAppointmentsHint", {
              count: data.appointmentStats?.upcoming ?? 0,
            }),
      })
    }

    if (canViewQuotes) {
      items.push({
        key: "quotesAwaiting",
        label: t("agentKpi.quotesAwaiting"),
        value: isLoading ? skeletonValue : (data.quoteStats?.by_status?.sent ?? 0),
        sub: isLoading
          ? undefined
          : t("agentKpi.quotesAwaitingHint", {
              count: data.quoteStats?.expiring_soon ?? 0,
            }),
      })
    }

    // Commission — only for an agent actually enrolled in the ladder. A running
    // estimate: the base moves with lead reassignment until the run is finalized.
    const commission = data.myCommission
    if (commission?.participating) {
      items.push({
        key: "myCommission",
        label: t("agentKpi.myCommission"),
        value: isLoading
          ? skeletonValue
          : formatCurrencyStatic(commission.commission, commission.currency),
        sub: isLoading
          ? undefined
          : t("agentKpi.myCommissionHint", {
              collected: formatCurrencyStatic(
                commission.collected,
                commission.currency,
              ),
            }),
      })
    }

    return items
  }, [
    t,
    isLoading,
    data.leadStats,
    data.appointmentStats,
    data.quoteStats,
    data.myCommission,
    dueCount,
    canViewLeads,
    canViewReminders,
    canViewAppointments,
    canViewQuotes,
    queueEnabled,
  ])

  // Row 2 pairs the schedule with the follow-ups rail; when only one survives
  // gating it takes the full width instead of leaving dead columns.
  const showDayRow = canViewAppointments || canViewReminders
  const dayRowSplit = canViewAppointments && canViewReminders

  return (
    <ApolloPageShell>
      <ApolloPageHeader
        eyebrow={`${t("page.eyebrow")} · ${today}`}
        title={t("variants.agent.title")}
        stats={statItems.length > 0 ? <StatsMetaline items={statItems} /> : undefined}
      />

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* My Tasks — self-gating (tasks_enabled + tasks.view.assigned). */}
          {queueEnabled ? <AgentWorkQueuePanel /> : <MyTasksWidget />}

          {/* Row 1 — target hero (2fr) + leaderboard (1fr); both are targets.view */}
          {canViewTargets && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <TargetProgressWidget
                  data={data.myProgress}
                  isLoading={isLoading}
                  onRefresh={handleRefreshProgress}
                />
              </div>
              <div className="lg:col-span-1">
                <LeaderboardWidget data={data.leaderboard} isLoading={isLoading} />
              </div>
            </div>
          )}

          {/* Row 2 — my day: today's schedule (2fr) + follow-ups due (1fr) */}
          {showDayRow && (
            <div
              className={cn(
                "grid gap-4 md:gap-6",
                dayRowSplit ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1",
              )}
            >
              {canViewAppointments && (
                <TodayScheduleTimeline
                  userId={myUserId}
                  className={dayRowSplit ? "lg:col-span-2" : undefined}
                />
              )}
              {canViewReminders && (
                <FollowUpsWidget
                  legacy={queueEnabled}
                  reminders={data.reminders ?? []}
                  isLoading={isLoading}
                  className={dayRowSplit ? "lg:col-span-1" : undefined}
                />
              )}
            </div>
          )}

          {/* Row 3 — my pipeline (server-scoped to the agent's own leads) */}
          {canViewLeads && !queueEnabled && showLeadsStatusChart && (
            <LeadsStatusChart data={data.leadStats} isLoading={isLoading} />
          )}
        </div>
      </div>
    </ApolloPageShell>
  )
}
